// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useClawbackReceiverDelegations } from '@hooks/queries/registry/clawback/useClawbackReceiverDelegations';
import useServiceUpgradeConsents from '@hooks/queries/registry/onboarding/useServiceUpgradeConsents';
import { useRegistrarHoldingOperationsDelegations } from '@hooks/queries/registry/registrar/useRegistrarHoldingOperationsDelegationsByParty';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { ClientOnboarderService } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/ClientOnboarder';
import { EndInvestorExistingContracts } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Offboarding';
import { ContractId, emptyMap, Map as DamlMap } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import Loading from '../../../components/Loading';
import { useLedgerExercise } from '../../../hooks/mutations/ledger/useLedgerExercise';
import useMutate from '../../../hooks/mutations/useMutate';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import useAllowLists from '../../../hooks/queries/registry/allowList/useAllowLists';
import useEndInvestorOnboardingOffers from '../../../hooks/queries/registry/onboarding/useEndInvestorOnboardingOffers';
import useEndInvestorOnboardingRequests from '../../../hooks/queries/registry/onboarding/useEndInvestorOnboardingRequests';
import { useParticipantServices } from '../../../hooks/queries/registry/onboarding/useParticipantServices';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import { ERR_OFFBOARD_PARTICIPANT, SUC_OFFBOARD_PARTICIPANT } from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  clientOnboarderServiceContractId: string;
};

export const OffboardParticipantDialog = ({
  open,
  onClose,
  clientOnboarderServiceContractId,
}: DialogProps) => {
  const [participant, setParticipant] = useState<string>('');
  const mutate = useMutate();
  const offboardParticipantMutation = useLedgerExercise(
    ClientOnboarderService.ClientOnboarderService_OffboardParticipant,
  );
  const credentials = useCredentials();
  const participantServices = useParticipantServices();
  const participantAllowLists = useAllowLists();
  const holdings = useHoldings();
  const clawbackDelegations = useClawbackReceiverDelegations();
  const registrarHoldingOperationsDelegations = useRegistrarHoldingOperationsDelegations();
  const endInvestorOnboardingOffers = useEndInvestorOnboardingOffers();
  const endInvestorOnboardingRequests = useEndInvestorOnboardingRequests();
  const serviceUpgradeConsents = useServiceUpgradeConsents();

  const resetState = useCallback(() => {
    setParticipant('');
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // Find participant services for the selected participant
  const participantServiceCids = useMemo(
    () =>
      participantServices.data
        ?.filter((svc) => svc.payload.participant === participant)
        .map((svc) => svc.contractId) ?? [],
    [participantServices.data, participant],
  );

  // Find participant's own credentials
  const participantCredentialCids = useMemo(
    () =>
      credentials.data
        ?.filter((cred) => cred.payload.holder === participant)
        .map((cred) => cred.contractId) ?? [],
    [credentials.data, participant],
  );

  // Find allow-lists owned by the participant
  const participantAllowListCids = useMemo(
    () =>
      participantAllowLists.data
        ?.filter((al) => al.payload.owner === participant)
        .map((al) => al.contractId) ?? [],
    [participantAllowLists.data, participant],
  );

  // Filter holdings associated with the participant
  const participantHoldings = useMemo(
    () => holdings.data?.filter((h) => h.payload.participant === participant) ?? [],
    [holdings.data, participant],
  );

  const participantClawbackReceiverDelegationCids = useMemo(
    () =>
      clawbackDelegations.data
        ?.filter((d) => d.payload.delegator === participant)
        .map((d) => d.contractId) ?? [],
    [clawbackDelegations.data, participant],
  );

  const participantRegistrarHoldingOperationsDelegationCids = useMemo(
    () =>
      registrarHoldingOperationsDelegations.data
        ?.filter((d) => d.payload.delegator === participant)
        .map((d) => d.contractId) ?? [],
    [registrarHoldingOperationsDelegations.data, participant],
  );

  const participantUpgradeConsentCids = useMemo(
    () =>
      serviceUpgradeConsents.data
        ?.filter((c) => c.payload.authorizer === participant)
        .map((c) => c.contractId) ?? [],
    [serviceUpgradeConsents.data, participant],
  );

  // Build end investor contracts map keyed by end investor party
  const existingEndInvestorContracts = useMemo(() => {
    const endInvestorCredentials =
      credentials.data?.filter(
        (cred) => cred.payload.participant === participant && cred.payload.holder !== participant,
      ) ?? [];
    const endInvestorClawbackDelegations =
      clawbackDelegations.data?.filter(
        (d) => d.payload.participant === participant && d.payload.delegator !== participant,
      ) ?? [];
    const endInvestorRegistrarDelegations =
      registrarHoldingOperationsDelegations.data?.filter(
        (d) => d.payload.participant === participant && d.payload.delegator !== participant,
      ) ?? [];

    const endInvestorParties = new Set<string>();
    endInvestorCredentials.forEach((c) => endInvestorParties.add(c.payload.holder));
    endInvestorClawbackDelegations.forEach((d) => endInvestorParties.add(d.payload.delegator));
    endInvestorRegistrarDelegations.forEach((d) => endInvestorParties.add(d.payload.delegator));

    let result: DamlMap<string, EndInvestorExistingContracts> = emptyMap();
    for (const ei of endInvestorParties) {
      result = result.set(ei, {
        existingCredentials: endInvestorCredentials
          .filter((c) => c.payload.holder === ei)
          .map((c) => c.contractId),
        existingClawbackReceiverDelegations: endInvestorClawbackDelegations
          .filter((d) => d.payload.delegator === ei)
          .map((d) => d.contractId),
        existingRegistrarHoldingOperationsDelegations: endInvestorRegistrarDelegations
          .filter((d) => d.payload.delegator === ei)
          .map((d) => d.contractId),
        existingOrchestratorHoldingOperationsDelegations: [],
        existingServiceUpgradeConsents:
          serviceUpgradeConsents.data
            ?.filter((c) => c.payload.authorizer === ei)
            .map((c) => c.contractId) ?? [],
      });
    }
    return result;
  }, [
    credentials.data,
    clawbackDelegations.data,
    registrarHoldingOperationsDelegations.data,
    serviceUpgradeConsents.data,
    participant,
  ]);

  const endInvestorOnboardingOfferCids = useMemo(
    () =>
      endInvestorOnboardingOffers.data
        ?.filter((offer) => offer.payload.participant === participant)
        .map((offer) => offer.contractId) ?? [],
    [endInvestorOnboardingOffers.data, participant],
  );

  const endInvestorOnboardingRequestCids = useMemo(
    () =>
      endInvestorOnboardingRequests.data
        ?.filter((request) => request.payload.participant === participant)
        .map((request) => request.contractId) ?? [],
    [endInvestorOnboardingRequests.data, participant],
  );

  const offboardParticipant = useCallback(async () => {
    if (participantServiceCids.length === 0) {
      enqueueSnackbar('No participant service found for this participant', { variant: 'error' });
      return;
    }

    const payload = {
      cid: clientOnboarderServiceContractId as ContractId<ClientOnboarderService>,
      argument: {
        participant,
        participantServiceCids: {
          hd: participantServiceCids[0],
          tl: participantServiceCids.slice(1),
        },
        existingParticipantContracts: {
          existingCredentials: participantCredentialCids,
          existingAllowLists: participantAllowListCids,
          existingClawbackReceiverDelegations: participantClawbackReceiverDelegationCids,
          existingRegistrarHoldingOperationsDelegations:
            participantRegistrarHoldingOperationsDelegationCids,
          existingOrchestratorHoldingOperationsDelegations: [],
          existingServiceUpgradeConsents: participantUpgradeConsentCids,
        },
        existingEndInvestorContracts,
        pendingEndInvestorOnboardingContracts: {
          endInvestorOnboardingRequestCids: endInvestorOnboardingRequestCids,
          endInvestorOnboardingOfferCids: endInvestorOnboardingOfferCids,
        },
      },
    };

    await mutate(
      offboardParticipantMutation,
      payload,
      SUC_OFFBOARD_PARTICIPANT,
      ERR_OFFBOARD_PARTICIPANT,
    );
    handleClose();
  }, [
    participant,
    participantServiceCids,
    participantCredentialCids,
    participantAllowListCids,
    participantClawbackReceiverDelegationCids,
    participantRegistrarHoldingOperationsDelegationCids,
    participantUpgradeConsentCids,
    existingEndInvestorContracts,
    endInvestorOnboardingRequestCids,
    endInvestorOnboardingOfferCids,
    clientOnboarderServiceContractId,
    handleClose,
    mutate,
    offboardParticipantMutation,
  ]);

  const isLoadingQueries =
    credentials.isLoading ||
    participantServices.isLoading ||
    participantAllowLists.isLoading ||
    holdings.isLoading ||
    clawbackDelegations.isLoading ||
    registrarHoldingOperationsDelegations.isLoading ||
    endInvestorOnboardingOffers.isLoading ||
    endInvestorOnboardingRequests.isLoading ||
    serviceUpgradeConsents.isLoading;
  const noParticipantSet = participant === '';
  const noParticipantServiceFound = participant !== '' && participantServiceCids.length === 0;
  const hasHoldings = participantHoldings.length > 0;
  const mutationIsPending = offboardParticipantMutation.isPending;
  const disableSubmit =
    noParticipantSet ||
    noParticipantServiceFound ||
    hasHoldings ||
    mutationIsPending ||
    isLoadingQueries;

  return (
    <FormDialog
      open={open}
      title="Offboard Participant"
      onCancel={handleClose}
      onSubmit={offboardParticipant}
      submitName="Offboard"
      isSubmitDisabled={disableSubmit}
      submitButtonSxProps={{ color: 'error' }}
    >
      {isLoadingQueries ? (
        <Loading />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextInput
            enableTopLabel
            label="Participant Party ID"
            value={participant}
            setValue={setParticipant}
          />
          {participant && (
            <Box>
              {noParticipantServiceFound ? (
                <Typography variant="body2" color="error">
                  No participant service found for this participant.
                </Typography>
              ) : (
                <>
                  {hasHoldings && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      Cannot offboard participant with {participantHoldings.length} holding(s). All
                      holdings must be transferred or redeemed before offboarding.
                    </Alert>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Found {participantServiceCids.length} participant service(s).
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Found {participantCredentialCids.length} participant credential(s) to archive.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Found {existingEndInvestorContracts.entriesArray().length} end investor(s) to
                    offboard.
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </FormDialog>
  );
};

export default OffboardParticipantDialog;
